import { Button, IconButton } from "@design";
import { useQuery } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { listDataSourcesOptions } from "@/api";
import { withForm } from "@/components/form/form";
import { PageSection } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { CountedTextareaField } from "./CompanyFields";
import { newRowId, profileToForm, TERMINOLOGY_LIMIT } from "./companyForm";

/** Leadership team (repeatable rows), key terminology, knowledge base link. */
export const KnowledgeSections = withForm({
  defaultValues: profileToForm(null),
  render: function KnowledgeSectionsRender({ form }) {
    const { t } = useTranslation("adminCompany");
    const sources = useQuery(listDataSourcesOptions({ query: { page_size: 100 } }));
    const sourceOptions = [
      { value: "", label: t("knowledge.none") },
      ...(sources.data?.data ?? []).map((s) => ({ value: s.uuid, label: s.name })),
    ];
    return (
      <>
        <form.Field name="leadership" mode="array">
          {(list) => (
            <PageSection
              title={t("leadership.title")}
              description={t("leadership.description")}
              actions={
                <Button
                  variant="secondary"
                  size="sm"
                  leadingIcon={<Plus size={16} aria-hidden />}
                  onClick={() => list.pushValue({ id: newRowId(), name: "", title: "" })}
                >
                  {t("leadership.add")}
                </Button>
              }
            >
              {list.state.value.length === 0 && (
                <Text variant="body-sm" tone="tertiary">
                  {t("leadership.empty")}
                </Text>
              )}
              {list.state.value.map((row, i) => (
                <div key={row.id} className="flex items-end gap-2">
                  <div className="grid flex-1 grid-cols-1 gap-2 md:grid-cols-2">
                    <form.AppField name={`leadership[${i}].name`}>
                      {(field) => (
                        <field.TextField
                          label={t("leadership.name", { n: i + 1 })}
                          placeholder={t("placeholders.leaderName")}
                        />
                      )}
                    </form.AppField>
                    <form.AppField name={`leadership[${i}].title`}>
                      {(field) => (
                        <field.TextField
                          label={t("leadership.role", { n: i + 1 })}
                          placeholder={t("placeholders.leaderTitle")}
                        />
                      )}
                    </form.AppField>
                  </div>
                  <IconButton
                    icon={<Trash2 size={16} />}
                    aria-label={t("leadership.remove", { n: i + 1 })}
                    variant="ghost"
                    onClick={() => list.removeValue(i)}
                  />
                </div>
              ))}
            </PageSection>
          )}
        </form.Field>

        <PageSection title={t("terminology.title")} description={t("terminology.description")}>
          <form.Field name="keyTerminology">
            {(field) => (
              <CountedTextareaField
                field={field}
                label={t("fields.keyTerminology")}
                limit={TERMINOLOGY_LIMIT}
                placeholder={t("placeholders.keyTerminology")}
              />
            )}
          </form.Field>
        </PageSection>

        <PageSection title={t("knowledge.title")} description={t("knowledge.description")}>
          <form.AppField name="knowledgeBase">
            {(field) => <field.SelectField label={t("fields.knowledgeBase")} options={sourceOptions} />}
          </form.AppField>
        </PageSection>
      </>
    );
  },
});
